import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import styled from 'styled-components';

import classnames from 'classnames';
import CompactTopicsContext from 'components/customer/compact_topics_context';
import connect from 'components/lib/connect';
import Conversation from 'models/conversation';
import { CustomerAssigneeIcon, ConversationAssigneeIcon } from 'components/lib/assignment_menu/conversation_assignee';
import ConversationTopics from 'components/customer/conversation_topics';
import CustomerContext from 'components/customer/customer_context';
import { Device } from 'scripts/lib/device_detector';
import getSelectedTopicLabels from 'components/customer/conversation_history/topics_v2/get_selected_topic_labels';
import HotkeyLabel from 'components/hotkeys/hotkey_label';
import i18n from 'scripts/i18n/i18n';
import ProfileAvatar from 'components/lib/profile_avatar';
import SearchableConversationAssignmentMenu from 'components/lib/assignment_menu/searchable_conversation_assignment_menu';
import ShowReopenConversationModal from 'actions/conversation/show_reopen_conversation_modal';
import Tooltip, { FocusManager, HoverWrapper, Hoverer } from 'components/common/tooltip';
import useDevice from 'components/hooks/use_device';

export default function CompactFooterLeft() {
  const [suppressAssignment, setSuppressAssignment] = useState(false);
  const [assignmentMenuOpen, setAssignmentMenuOpen] = useState(false);
  const [suppressTopics, setSuppressTopics] = useState(false);
  const [topicsMenuOpen, setTopicsMenuOpen] = useState(false);
  const [topicsOpenRequestedAt, setTopicsOpenRequestedAt] = useState(null);
  const isPhone = useDevice() === Device.Phone;

  const handleTopicsCellClick = useCallback(event => {
    // Skip if the click originated from inside the multi-select — it handles its own click
    if (event.target.closest('[data-aid="conversationTopic-multi"]')) {
      return;
    }

    setTopicsOpenRequestedAt(Date.now().toString());
  }, []);

  return (
    <CustomerContext.Consumer>
      {({ latestConversationId, latestConversationStatus }) => {
        const isClosed = latestConversationStatus === Conversation.Status.CLOSED;

        return (
          <CompactLeftColumn>
            <AssignmentTooltipCell
              onMouseDown={() => setSuppressAssignment(true)}
              onMouseMove={suppressAssignment ? () => setSuppressAssignment(false) : undefined}
            >
              <AssignmentTooltip
                delay={800}
                message={
                  isPhone || suppressAssignment || assignmentMenuOpen ? null : (
                    <HotkeyLabel hotkey="alt+a" label="Change assignee" />
                  )
                }
                position="top"
              >
                <CompactAssignmentCell>
                  <CompactAssignment conversationId={latestConversationId} onMenuOpenChange={setAssignmentMenuOpen} />
                </CompactAssignmentCell>
              </AssignmentTooltip>
            </AssignmentTooltipCell>
            <CellDivider />
            <TopicsTooltipCell
              onMouseDown={isClosed ? undefined : () => setSuppressTopics(true)}
              onMouseMove={!isClosed && suppressTopics ? () => setSuppressTopics(false) : undefined}
            >
              {isClosed ? (
                <ClosedTopicsSummary conversationId={latestConversationId} />
              ) : (
                <TopicsTooltip
                  delay={800}
                  message={
                    isPhone || suppressTopics || topicsMenuOpen ? null : (
                      <HotkeyLabel hotkey="alt+t" label="Edit topics" />
                    )
                  }
                  position="top"
                >
                  <CompactTopicsCell onClick={handleTopicsCellClick}>
                    <CompactTopicsContext.Provider value>
                      <ConversationTopics
                        onMenuOpenChange={setTopicsMenuOpen}
                        openRequestedAt={topicsOpenRequestedAt}
                      />
                    </CompactTopicsContext.Provider>
                  </CompactTopicsCell>
                </TopicsTooltip>
              )}
            </TopicsTooltipCell>
          </CompactLeftColumn>
        );
      }}
    </CustomerContext.Consumer>
  );
}

function CompactAssignment({ conversationId, onMenuOpenChange }) {
  return (
    <CompactFooterAssignmentHost>
      <SearchableConversationAssignmentMenu
        conversationId={conversationId}
        onMenuOpenChange={onMenuOpenChange}
        renderAssignee={assigneeProps => <CompactAssignmentRow {...assigneeProps} />}
      />
    </CompactFooterAssignmentHost>
  );
}

function CompactAssignmentRow({
  className,
  conversationId,
  conversationAssignee: conversationAssigneeProp,
  customerAssignee,
  onClick,
}) {
  const hasCustomerAssignee = !_.isEmpty(customerAssignee);

  // When there's no conversation, fall back to customerAssignee (dedicated hero / hero inbox),
  // matching the behavior in SearchableConversationAssignment.getAssigneeInfo()
  const conversationAssignee =
    !conversationId && hasCustomerAssignee ? customerAssignee : conversationAssigneeProp || {};
  const { agentName, avatarImage, groupName, isAssigneeCurrentAgent } = conversationAssignee;

  const displayName = agentName ? `${agentName}${isAssigneeCurrentAgent ? ' (You)' : ''}` : groupName || 'Not assigned';

  const isSameAssignee =
    hasCustomerAssignee &&
    customerAssignee.agentId === conversationAssignee.agentId &&
    customerAssignee.groupId === conversationAssignee.groupId;
  const showDedicatedHero = !!conversationId && hasCustomerAssignee && !isSameAssignee;

  let secondLine = null;
  if (!conversationId && hasCustomerAssignee) {
    secondLine = customerAssignee.agentName ? 'Dedicated Hero' : 'Hero Inbox';
  } else if (groupName && agentName) {
    secondLine = groupName;
  }

  const assigneeClassName = 'searchableConversationAssignment-assignee';
  const customerAssigneeText = hasCustomerAssignee
    ? customerAssignee.agentName
      ? 'Dedicated Hero'
      : 'Hero Inbox'
    : '';

  return (
    <CompactAssignmentRowRoot
      className={classnames(className, {
        customerAssigneeIcon: showDedicatedHero,
        'currentAssignee-customerAssignee': isSameAssignee,
      })}
      data-aid="assignment-assignee-display"
      onClick={onClick}
    >
      {showDedicatedHero ? (
        <AvatarGroup>
          <CustomerAssigneeIcon
            className={assigneeClassName}
            customerAssignee={customerAssignee}
            customerAssigneeText={customerAssigneeText}
            hasCustomerAssignee={hasCustomerAssignee}
            hideCustomerAssignee={false}
            hideTooltips={false}
          />
          <ConversationAssigneeIcon
            className={assigneeClassName}
            conversationAssignee={conversationAssignee}
            customerAssigneeText={customerAssigneeText}
            hideCustomerAssignee={false}
            hideTooltips={false}
            isAssigneeCustomerAssignee={false}
          />
        </AvatarGroup>
      ) : (
        <ProfileAvatar profile={{ name: agentName || groupName || '', image: avatarImage }} />
      )}
      <CompactAssignmentText>
        <AgentNameLine>{displayName}</AgentNameLine>
        {secondLine && <InboxNameLine>{secondLine}</InboxNameLine>}
      </CompactAssignmentText>
    </CompactAssignmentRowRoot>
  );
}

function ClosedTopicsSummaryBase({ topicNames, onClick }) {
  const isPhone = useDevice() === Device.Phone;

  // Tooltip keeps showing while its child holds focus, and activating focuses this cell, so
  // the tooltip stays suppressed until the next hover or focus.
  const [isSuppressed, setIsSuppressed] = useState(false);

  const handleActivate = useCallback(() => {
    setIsSuppressed(true);
    onClick && onClick();
  }, [onClick]);

  const handleKeyDown = useCallback(
    event => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleActivate();
      }
    },
    [handleActivate]
  );

  const hasTopics = topicNames.length > 0;
  let label;
  if (!hasTopics) {
    label = i18n.t('No topics');
  } else if (topicNames.length === 1) {
    label = topicNames[0];
  } else {
    label = i18n.t('{{count}} Topics', { count: topicNames.length });
  }

  const topicList =
    isPhone || isSuppressed || !hasTopics ? null : (
      <ClosedTopicsList data-aid="closedTopicsSummary-tooltip">
        {topicNames.map((name, index) => (
          <ClosedTopicsListItem key={`${index}-${name}`}>{name}</ClosedTopicsListItem>
        ))}
      </ClosedTopicsList>
    );

  return (
    <TopicsTooltip bounds={getWindowBounds()} delay={300} message={topicList} position="top">
      <CompactTopicsCell
        onMouseDown={() => setIsSuppressed(true)}
        onMouseEnter={isSuppressed ? () => setIsSuppressed(false) : undefined}
      >
        <FocusManager>
          {({ onBlur, onFocus }) => (
            <ClosedTopicsDisplay
              aria-label={hasTopics ? `${i18n.t('Topics')}: ${topicNames.join(', ')}` : undefined}
              data-aid="closedTopicsSummary"
              onBlur={event => {
                setIsSuppressed(false);
                onBlur(event);
              }}
              onClick={handleActivate}
              onFocus={onFocus}
              onKeyDown={handleKeyDown}
              role="button"
              tabIndex={0}
            >
              {label}
            </ClosedTopicsDisplay>
          )}
        </FocusManager>
      </CompactTopicsCell>
    </TopicsTooltip>
  );
}

ClosedTopicsSummaryBase.propTypes = {
  onClick: PropTypes.func,
  topicNames: PropTypes.arrayOf(PropTypes.string).isRequired,
};

// Keeps a long topic list from running off the top of the screen.
function getWindowBounds() {
  return {
    left: 8,
    right: window.innerWidth - 8,
    top: 8,
  };
}

const ClosedTopicsSummary = connect(
  ({ getProvider }, { conversationId }) => {
    const conversation = getProvider('conversations').findBy({ id: conversationId });
    const customAttributesProvider = getProvider('customAttributes');
    const topicsProvider = getProvider('topics');

    const findOption = id => {
      const topic = topicsProvider.findBy({ id });
      if (topic) {
        return { name: topic.getNameWithAncestry() };
      }

      const attribute = customAttributesProvider.findBy({ id });
      return attribute && { disabled: attribute.deactivated, name: attribute.label };
    };

    return {
      topicNames: getSelectedTopicLabels({
        attributeValues: (conversation && conversation.customAttributes) || [],
        findOption,
        optionIds: (conversation && conversation.topicIds) || [],
      }),
    };
  },
  executeAction => ({
    onClick: () => executeAction(ShowReopenConversationModal),
  })
)(ClosedTopicsSummaryBase);

const assigneeDetailsShape = PropTypes.shape({
  agentId: PropTypes.string,
  agentName: PropTypes.string,
  avatarImage: PropTypes.string,
  groupId: PropTypes.string,
  groupName: PropTypes.string,
  isAssigneeCurrentAgent: PropTypes.bool,
});

CompactAssignment.propTypes = {
  conversationId: PropTypes.string,
  onMenuOpenChange: PropTypes.func,
};

CompactAssignmentRow.propTypes = {
  className: PropTypes.string,
  conversationId: PropTypes.string,
  conversationAssignee: assigneeDetailsShape,
  customerAssignee: assigneeDetailsShape,
  onClick: PropTypes.func,
};

/* --- Styled Components --- */

const hoverBorderStyle = `
  border-top: 2px solid transparent;
  transition: border-color 150ms ease;

  &:hover {
    border-top-color: var(--color-green400);
  }
`;

const CompactLeftColumn = styled.div`
  align-items: stretch;
  display: flex;
  flex-shrink: 0;
  height: 100%;
  min-width: 200px;
  width: 360px;
`;

const CellDivider = styled.div`
  align-self: center;
  background: var(--color-gray300);
  flex-shrink: 0;
  height: 32px;
  width: 1px;
`;

const CompactAssignmentCell = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
  ${hoverBorderStyle}
`;

const CompactFooterAssignmentHost = styled.div`
  flex: 1;
  min-width: 0;
  width: 100%;

  && .searchableConversationAssignment {
    min-width: unset;
    width: unset;
  }
`;

const CompactAssignmentRowRoot = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  height: 100%;
  min-width: 0;

  .profileAvatar-iconWrapper,
  .searchableConversationAssignment-assignee-inboxIconContainer {
    flex-shrink: 0;
  }

  /* Hover scale on avatar when assignee is also the dedicated hero */
  &.currentAssignee-customerAssignee .profileAvatar-iconWrapper {
    transition: transform 0.2s ease-in-out;
    &:hover {
      transform: scale(1.15);
    }
  }
`;

const AvatarGroup = styled.div`
  align-items: center;
  display: flex;
  flex-shrink: 0;

  /* Override flex: 1 inherited from flexCellTooltip on ancestor tooltip */
  && ${HoverWrapper}, && ${Hoverer} {
    flex: initial;
  }

  /* Overlap the conversation assignee avatar onto the customer assignee avatar */
  .conversationAssignee.profileAvatar-iconWrapper,
  .conversationAssignee.searchableConversationAssignment-assignee-inboxIconContainer {
    margin-left: -16px;
  }
`;

const CompactAssignmentText = styled.div`
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  overflow: hidden;
`;

const AgentNameLine = styled.div`
  color: var(--color-green400);
  font-size: var(--font-size-base);
  font-weight: 500;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const InboxNameLine = styled.div`
  color: var(--color-gray600);
  font-size: ${p => p.theme.fontSize.reduced};
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ClosedTopicsDisplay = styled.div`
  align-items: center;
  color: var(--color-gray600);
  cursor: pointer;
  display: flex;
  flex: 1;
  font-size: var(--font-size-base);
  height: 100%;
  min-width: 0;
  overflow: hidden;
  padding: 0 8px;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ClosedTopicsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: calc(100vh - 120px);
  max-width: 280px;
  overflow-y: auto;
`;

const ClosedTopicsListItem = styled.div`
  overflow-wrap: anywhere;
  /* Overrides the tooltip's pre-line so topic names wrap as normal text */
  white-space: normal;
`;

const CompactTopicsCell = styled.div`
  align-items: center;
  cursor: pointer;
  display: flex;
  flex: 1;
  min-width: 0;
  overflow: visible;
  position: relative;
  ${hoverBorderStyle}

  .conversation-topic {
    flex: 1 1 auto;
    min-width: 0;
  }

  .conversationTopic-multi {
    flex-basis: auto;
    max-width: 100%;
    min-width: 0;
    overflow: visible;
  }

  .conversationTopic-menuOpener {
    padding: 0 8px;
  }

  .conversationTopic-display-value {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .conversationTopic-multi::after {
    display: none;
  }

  .conversationTopic-menu-container {
    bottom: calc(100% + 8px);
    left: 0;
    z-index: 40;
  }
`;

const flexCellTooltip = `
  display: flex;
  min-width: 0;

  ${HoverWrapper}, ${Hoverer} {
    display: flex;
    flex: 1;
    min-width: 0;
  }
`;

const AssignmentTooltipCell = styled.div`
  display: flex;
  flex: 3 1 60%;
  min-width: 0;
`;

const TopicsTooltipCell = styled.div`
  display: flex;
  flex: 2 1 40%;
  min-width: 0;
`;

const cellTooltip = `
  flex: 1;
  ${flexCellTooltip}
`;

const AssignmentTooltip = styled(Tooltip)`
  ${cellTooltip}
`;

const TopicsTooltip = styled(Tooltip)`
  ${cellTooltip}
`;
